import { useState } from 'react'
import { CheckIcon, PlusCircle } from 'lucide-react'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from 'shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from 'shared/components/ui/popover'
import { Separator } from 'shared/components/ui/separator'
import { cn } from 'shared/lib/utils'

interface FacetedFilterProps {
	values: string[] | undefined
	setValues: React.Dispatch<React.SetStateAction<string[] | undefined>>
	title?: string
	isRadio?: boolean
	options: {
		label: string
		value: string
		icon?: React.ComponentType<{ className?: string }>
	}[]
	extraProps?: {
		modal?: boolean
	}
}

export function FacetedFilter({
	values,
	setValues,
	title,
	options,
	isRadio = false,
	extraProps,
}: FacetedFilterProps) {
	const [search, setSearch] = useState('')

	const selectedValues = new Set(values)
	return (
		<Popover modal={extraProps?.modal}>
			<PopoverTrigger asChild>
				<Button variant='outline' size='sm' className='h-8 border-dashed'>
					<PlusCircle className='mr-2 h-4 w-4' />
					<span className='lowercase first-letter:uppercase'>{title}</span>
					{selectedValues?.size > 0 && (
						<>
							<Separator orientation='vertical' className='mx-2 h-full' />
							<Badge
								variant='secondary'
								className='rounded-sm px-1 font-normal lg:hidden'
							>
								{selectedValues.size}
							</Badge>
							<div className='hidden space-x-1 lg:flex'>
								{selectedValues.size > 2 ? (
									<Badge
										variant='secondary'
										className='rounded-sm px-1 font-normal'
									>
										{selectedValues.size} selected
									</Badge>
								) : (
									options
										.filter((option) => selectedValues.has(option.value))
										.map((option) => (
											<Badge
												variant='secondary'
												key={option.value}
												className='rounded-sm px-1 font-normal'
											>
												{option.label}
											</Badge>
										))
								)}
							</div>
						</>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-[200px] p-0' align='start'>
				<Command>
					<CommandInput
						placeholder={title}
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>Нет результатов.</CommandEmpty>
						<CommandGroup>
							{options.map((option) => {
								const isSelected = selectedValues.has(option.value)
								return (
									<CommandItem
										key={option.value}
										onSelect={() => {
											if (isSelected) {
												selectedValues.delete(option.value)
											} else {
												selectedValues.add(option.value)
											}
											const filterValues = Array.from(selectedValues)
											setValues(
												isRadio
													? [option.value]
													: filterValues.length
														? filterValues
														: undefined,
											)
										}}
										className='gap-2'
									>
										<div
											className={cn(
												'absolute w-1/2 left-0 inset-y-0',
												isRadio && 'w-full',
											)}
										/>

										{!isRadio && (
											<div
												className='absolute w-1/2 right-0 inset-y-0 cursor-pointer peer'
												onClick={(e) => {
													e.stopPropagation()
													setValues([option.value])
												}}
											/>
										)}

										<div
											className={cn(
												'flex h-4 w-4 items-center justify-center rounded-sm border border-primary peer-hover:rounded-full pointer-events-none z-10',
												isRadio && 'rounded-full',
												isSelected
													? 'bg-primary text-primary-foreground'
													: 'opacity-50 [&_svg]:invisible',
											)}
										>
											<CheckIcon className='size-4 text-background' />
										</div>

										{option.icon && (
											<option.icon className='mr-2 h-4 w-4 text-muted-foreground pointer-events-none z-10' />
										)}
										<span className='pointer-events-none z-10'>
											{option.label}
										</span>
									</CommandItem>
								)
							})}
						</CommandGroup>
					</CommandList>
					{!isRadio && (
						<>
							<Separator />
							<CommandGroup forceMount>
								<CommandItem
									onSelect={() => {
										if (!search.trim()) {
											setValues(
												selectedValues.size
													? undefined
													: options.map((option) => option.value),
											)
											return
										}

										const filteredOptions = options
											.filter(({ label }) => {
												if (
													label
														.toLocaleLowerCase()
														.includes(search.toLocaleLowerCase())
												)
													return 1
												return 0
											})
											.map((option) => option.value)

										setValues(filteredOptions)
									}}
									className='justify-center text-center [&_svg]:hidden'
								>
									{search
										? 'Выбрать отфильтрованные'
										: selectedValues.size
											? 'Очистить все'
											: 'Выбрать все'}
								</CommandItem>
							</CommandGroup>
						</>
					)}
				</Command>
			</PopoverContent>
		</Popover>
	)
}
